import type { LanguageContent } from './types.js';

let finnishWords: any[] | null = null;

async function loadFinnishWords(): Promise<any[]> {
  if (typeof window === 'undefined') return [];
  if (!finnishWords) {
    try {
      const response = await fetch('/api/vocabulary/fi_v2.json');
      if (response.ok) {
        finnishWords = await response.json();
      }
    } catch {
      finnishWords = [];
    }
  }
  return finnishWords ?? [];
}

function extractVocabularyByCEFR(data: any[], cefrLevel: string) {
  if (!Array.isArray(data)) return [];
  return data
    .filter((word: any) => word.cefr_level === cefrLevel && word.useful_for_flashcard)
    .sort((a: any, b: any) => (b.word_frequency || 0) - (a.word_frequency || 0))
    .map((word: any) => ({ term: word.word, meaning: word.english_translation }));
}

async function getVocabularyData() {
  const words = await loadFinnishWords();
  return {
    A1: extractVocabularyByCEFR(words, 'A1'),
    A2: extractVocabularyByCEFR(words, 'A2'),
    B1: extractVocabularyByCEFR(words, 'B1'),
    B2: extractVocabularyByCEFR(words, 'B2'),
    C1: extractVocabularyByCEFR(words, 'C1'),
    C2: extractVocabularyByCEFR(words, 'C2'),
  };
}

export const finnishContent: LanguageContent = {
  language: 'Finnish',
  code: 'fi',
  pronouns: ['minä', 'sinä', 'hän', 'me', 'te', 'he'],
  tenses: ['present', 'conditional', 'present_perfect'],
  grammar: [
    {
      title: 'Vowel harmony',
      explanation:
        'Finnish words use either back vowels (a, o, u) or front vowels (ä, ö, y). Suffixes change to match: -ssa/-ssä, -lla/-llä, etc.',
      examples: ['talossa (in a house)', 'metsässä (in a forest)', 'autolla (by car)', 'pyörällä (by bike)'],
    },
    {
      title: 'Personal pronouns',
      explanation:
        'Finnish subject pronouns: minä (I), sinä (you), hän (he/she), me (we), te (you all), he (they). Hän is gender-neutral.',
      examples: ['Minä olen suomalainen.', 'Hän on opettaja.', 'Me asumme Helsingissä.'],
    },
    {
      title: 'Present tense (Preesens)',
      explanation:
        'Add personal endings to the verb stem. The stem is usually the infinitive minus -a/-ä (with possible consonant gradation).',
      examples: ['puhua → puhun, puhut, puhuu, puhumme, puhutte, puhuvat'],
    },
    {
      title: 'Negative verb',
      explanation:
        'Finnish negation uses the auxiliary verb "ei" (not), which changes by person. The main verb takes the connective form.',
      examples: ['en puhu, et puhu, ei puhu, emme puhu, ette puhu, eivät puhu'],
    },
    {
      title: 'Noun cases',
      explanation:
        'Finnish has 15 grammatical cases. The most common are nominative, genitive (-n), partitive (-a/-ä), and the locative cases.',
      examples: ['talo (house), talon (of the house), taloa (some house), talossa (in a house)'],
    },
  ],
  vocabulary: async () => {
    const data = await getVocabularyData();
    return [
      { title: 'A1 - Beginner',           words: data.A1 },
      { title: 'A2 - Elementary',          words: data.A2 },
      { title: 'B1 - Intermediate',        words: data.B1 },
      { title: 'B2 - Upper Intermediate',  words: data.B2 },
      { title: 'C1 - Advanced',            words: data.C1 },
      { title: 'C2 - Proficient',          words: data.C2 },
    ];
  },
  sentences: [
    { prompt: 'Minä __ kahvia. (juoda)', answer: 'juon' },
    { prompt: 'Hän __ Helsingissä. (asua)', answer: 'asuu' },
    { prompt: '__ sinä suomea? (puhua)', answer: 'puhutko' },
  ],
  comprehension: {
    paragraph:
      'Liisa asuu Helsingissä. Hän käy töissä joka päivä. Aamuisin hän juo kahvia ja lukee uutisia. Illalla hän kävelee järvellä ja katsoo auringonlaskua.',
    questions: [
      { q: 'Missä Liisa asuu?', a: 'Helsingissä.' },
      { q: 'Mitä hän tekee aamuisin?', a: 'Juo kahvia ja lukee uutisia.' },
      { q: 'Mitä hän tekee illalla?', a: 'Kävelee järvellä.' },
    ],
  },
  speaking: [
    {
      topic: 'Esittäytyminen',
      instruction: 'Esittele itsesi 3–4 lauseessa: nimi, kotipaikka, työ tai opiskelu, harrastukset.',
      starter: 'Hei, minun nimeni on... Asun... Työskentelen/Opiskelen... Harrastan...',
    },
    {
      topic: 'Kahvilassa',
      instruction: 'Tilaa kahvilassa. Sano mitä haluat ja kysy hintaa.',
    },
  ],
  verbs: [
    {
      infinitive: 'olla',
      english: 'to be',
      conjugations: {
        present: {
          minä: 'olen', sinä: 'olet', hän: 'on', me: 'olemme', te: 'olette', he: 'ovat',
        },
      },
    },
    {
      infinitive: 'puhua',
      english: 'to speak',
      conjugations: {
        present: {
          minä: 'puhun', sinä: 'puhut', hän: 'puhuu', me: 'puhumme', te: 'puhutte', he: 'puhuvat',
        },
      },
    },
    {
      infinitive: 'mennä',
      english: 'to go',
      conjugations: {
        present: {
          minä: 'menen', sinä: 'menet', hän: 'menee', me: 'menemme', te: 'menette', he: 'menevät',
        },
      },
    },
    {
      infinitive: 'tulla',
      english: 'to come',
      conjugations: {
        present: {
          minä: 'tulen', sinä: 'tulet', hän: 'tulee', me: 'tulemme', te: 'tulette', he: 'tulevat',
        },
      },
    },
    {
      infinitive: 'haluta',
      english: 'to want',
      conjugations: {
        present: {
          minä: 'haluan', sinä: 'haluat', hän: 'haluaa', me: 'haluamme', te: 'haluatte', he: 'haluavat',
        },
      },
    },
    {
      infinitive: 'voida',
      english: 'to be able to',
      conjugations: {
        present: {
          minä: 'voin', sinä: 'voit', hän: 'voi', me: 'voimme', te: 'voitte', he: 'voivat',
        },
      },
    },
  ],
};
